import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";

// CSV: isin,holding_name,esg_score,data_source,excl_weapons,excl_coal,excl_tobacco,excl_other,excl_other_reason,as_of_date
export default function EsgScoresUploader() {
  const [csv, setCsv] = useState("");
  const [busy, setBusy] = useState(false);
  const [count, setCount] = useState(0);
  const { toast } = useToast();

  const loadStats = async () => {
    const { count } = await supabase.from("policy_esg_scores" as any).select("*", { count: "exact", head: true });
    setCount(count ?? 0);
  };
  useEffect(() => { loadStats(); }, []);

  const toBool = (v: string) => /^(1|true|yes|y)$/i.test((v ?? "").trim());

  const upload = async () => {
    setBusy(true);
    try {
      const lines = csv.trim().split(/\r?\n/).filter(l => l.trim());
      if (!lines.length) throw new Error("No rows.");
      const data = lines[0].toLowerCase().includes("isin") ? lines.slice(1) : lines;

      const rows = data.map(l => {
        const p = l.split(",").map(s => s.trim());
        const [isin, holding_name, scoreRaw, source, w, c, t, o, oReason, asOf] = p;
        if (!isin) throw new Error(`Bad row: ${l}`);
        const score = scoreRaw ? Number(scoreRaw) : null;
        return {
          isin: isin.toUpperCase(),
          holding_name: holding_name || null,
          esg_score: score,
          data_source: source || null,
          excl_controversial_weapons: toBool(w),
          excl_thermal_coal: toBool(c),
          excl_tobacco: toBool(t),
          excl_other: toBool(o),
          excl_other_reason: oReason || null,
          as_of_date: asOf || null,
        };
      });

      const chunkSize = 500;
      let total = 0;
      for (let i = 0; i < rows.length; i += chunkSize) {
        const chunk = rows.slice(i, i + chunkSize);
        const { error } = await supabase.from("policy_esg_scores" as any).upsert(chunk, { onConflict: "isin" });
        if (error) throw error;
        total += chunk.length;
      }
      toast({ title: "Upload complete", description: `${total} ESG records upserted.` });
      setCsv("");
      loadStats();
    } catch (e: any) {
      toast({ title: "Upload failed", description: e.message ?? String(e), variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader><CardTitle>§14 ESG Scores &amp; Exclusions</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="text-xs text-muted-foreground space-y-1">
          <p>Per-ISIN ESG score and exclusion flags. Used by the breach scanner to flag holdings violating §14 screens.</p>
          <p><strong>CSV:</strong> <code>isin,holding_name,esg_score,data_source,excl_weapons,excl_coal,excl_tobacco,excl_other,excl_other_reason,as_of_date</code></p>
          <p>Booleans accept <code>1/0</code>, <code>true/false</code>, <code>yes/no</code>.</p>
        </div>
        <div className="text-xs border rounded p-3 bg-muted/30">
          <span className="text-muted-foreground">ISINs covered:</span>{" "}
          <span className="font-medium tabular-nums">{count.toLocaleString()}</span>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Paste CSV</Label>
          <Textarea
            value={csv}
            onChange={(e) => setCsv(e.target.value)}
            placeholder={"isin,holding_name,esg_score,data_source,excl_weapons,excl_coal,excl_tobacco,excl_other,excl_other_reason,as_of_date\nGB00B7VVMZ49,Vanguard FTSE All-World,7.2,MSCI,0,0,0,0,,2026-04-30"}
            rows={8}
            className="font-mono text-xs"
          />
        </div>
        <div className="flex gap-2">
          <Button onClick={upload} disabled={busy || !csv.trim()}>{busy ? "Uploading…" : "Upload"}</Button>
          <Button variant="outline" onClick={() => setCsv("")} disabled={busy}>Clear</Button>
        </div>
      </CardContent>
    </Card>
  );
}
